import jsPDF from 'jspdf';

export const generateRentalContractPDF = (rentalData, customerData, vehicleData, companyData) => {
  const doc = new jsPDF();
  const vehiclesList = Array.isArray(vehicleData) ? vehicleData : (vehicleData ? [vehicleData] : []);
  
  // Configurazione colori aziendali
  const primaryColor = [25, 118, 210]; // Blu Material-UI
  const secondaryColor = [76, 175, 80]; // Verde
  const textColor = [33, 33, 33]; // Grigio scuro
  const lightGray = [245, 245, 245];
  
  // Configurazione dimensioni
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const lineHeight = 4.5;
  let yPosition = margin;
  
  // Funzione per aggiungere header con sfondo colorato
  const addHeader = (text, y, color = primaryColor) => {
    doc.setFillColor(...color);
    doc.rect(margin, y - 5, pageWidth - margin * 2, 12, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(text, pageWidth / 2, y + 2, { align: 'center' });
    doc.setTextColor(...textColor);
    return y + 15;
  };
  
  // Funzione per aggiungere testo formattato
  const addText = (text, x, y, options = {}) => {
    const { fontSize = 10, fontStyle = 'normal', align = 'left', maxWidth = pageWidth - margin * 2, color = textColor } = options;
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', fontStyle);
    doc.setTextColor(...color);
    
    if (align === 'center') {
      doc.text(text, pageWidth / 2, y, { align: 'center' });
    } else {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return y + (lines.length * lineHeight);
    }
    return y + lineHeight;
  };
  
  // Funzione per aggiungere campo con etichetta
  const addField = (label, value, x, y, options = {}) => {
    const { labelWidth = 60, valueWidth = 80, fontSize = 10 } = options;
    
    // Etichetta
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textColor);
    doc.text(label + ':', x, y);
    
    // Valore - converti sempre in stringa
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    const valueText = value ? String(value) : '________________________';
    doc.text(valueText, x + labelWidth, y);
    
    return y + lineHeight + 1;
  };
  
  // Funzione per aggiungere sezione con sfondo
  const addSection = (title, content, y) => {
    // Sfondo grigio chiaro
    doc.setFillColor(...lightGray);
    doc.rect(margin, y, pageWidth - margin * 2, 8, 'F');
    
    // Titolo sezione
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primaryColor);
    doc.text(title, margin + 5, y + 5);
    
    let currentY = y + 12;
    
    // Contenuto
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...textColor);
    
    content.forEach(line => {
      if (currentY > pageHeight - 30) {
        doc.addPage();
        currentY = margin;
      }
      currentY = addText(line, margin + 5, currentY, { fontSize: 9 });
    });
    
    return currentY + 3;
  };
  
  // Header principale
  yPosition = addHeader('CONTRATTO DI NOLEGGIO VEICOLO ELETTRICO', yPosition);
  yPosition += 3;
  
  // Informazioni contratto
  yPosition = addText(`Contratto n. ${Date.now().toString().slice(-6)}`, pageWidth - 80, yPosition, { fontSize: 10, align: 'right' });
  yPosition = addText(`Data: ${new Date().toLocaleDateString('it-IT')}`, pageWidth - 80, yPosition, { fontSize: 10, align: 'right' });
  yPosition += 6;
  
  // Sezione Noleggiatore
  yPosition = addHeader('NOLEGGIATORE', yPosition, secondaryColor);
  yPosition = addField('Ragione Sociale', companyData.name || 'ELEMOVE SRLS', margin, yPosition);
  yPosition = addField('Indirizzo', companyData.address || 'VIA RAIOLA 19, 80053 CASTELLAMMARE DI STABIA (NA)', margin, yPosition);
  yPosition = addField('P.IVA', companyData.vatNumber || '09624041217', margin, yPosition);
  yPosition = addField('Telefono', companyData.phone || '+39 081 123 4567', margin, yPosition);
  yPosition = addField('Email', companyData.email || 'info@elemove.it', margin, yPosition);
  yPosition += 6;
  
  // Sezione Cliente
  yPosition = addHeader('CLIENTE', yPosition);
  yPosition = addField('Nome e Cognome', `${customerData.firstName} ${customerData.lastName}`, margin, yPosition);
  yPosition = addField('Telefono', customerData.phone || '________________', margin, yPosition);
  yPosition = addField('Email', customerData.email || '________________', margin, yPosition);
  yPosition = addField('Documento', `${customerData.documentType || 'N/A'} N. ${customerData.documentNumber || '________________'}`, margin, yPosition);
  if (customerData.documentIssueDate || customerData.documentExpiryDate) {
    const docDates = [];
    if (customerData.documentIssueDate) docDates.push(`Rilascio: ${new Date(customerData.documentIssueDate).toLocaleDateString('it-IT')}`);
    if (customerData.documentExpiryDate) docDates.push(`Scadenza: ${new Date(customerData.documentExpiryDate).toLocaleDateString('it-IT')}`);
    yPosition = addField('', docDates.join(' - '), margin, yPosition);
  }
  if (customerData.documentIssuedBy) {
    yPosition = addField('Rilasciato da', customerData.documentIssuedBy, margin, yPosition);
  }
  yPosition += 6;
  
  // Sezione Veicolo/Veicoli (dati dal parco mezzi: tipo, modello, anno, numero di serie, colore)
  const vehicleSectionTitle = vehiclesList.length > 1 ? 'VEICOLI NOLEGGIATI' : 'VEICOLO NOLEGGIATO';
  yPosition = addHeader(vehicleSectionTitle, yPosition, secondaryColor);
  vehiclesList.forEach((v, idx) => {
    if (vehiclesList.length > 1) {
      yPosition = addText(`Veicolo ${idx + 1}: ${v.name || '—'}`, margin, yPosition, { fontSize: 10, fontStyle: 'bold' });
      yPosition += 1;
    }
    const typeLabel = v.type === 'ebike' ? 'Bicicletta Elettrica' : v.type === 'bike' ? 'Bicicletta' : v.type === 'scooter' ? 'Monopattino' : (v.type || '—');
    yPosition = addField('Tipo', typeLabel, margin, yPosition);
    yPosition = addField('Marca/Modello', v.model || '—', margin, yPosition);
    yPosition = addField('Anno', v.year ? String(v.year) : '—', margin, yPosition);
    yPosition = addField('Numero di Serie', v.serialNumber || '—', margin, yPosition);
    yPosition = addField('Colore', v.color || '—', margin, yPosition);
    yPosition = addField('Stato', v.condition === 'excellent' ? 'Ottimo' : v.condition === 'good' ? 'Buono' : v.condition || '—', margin, yPosition);
    if (vehiclesList.length > 1 && idx < vehiclesList.length - 1) yPosition += 3;
  });
  yPosition += 6;
  
  // Sezione Condizioni di Noleggio (tariffa = quella selezionata: giornaliera, settimanale o mensile)
  const tariffLabels = { giornaliero: 'Giornaliera', settimanale: 'Settimanale', mensile: 'Mensile' };
  const tariffType = rentalData.tariffType || 'giornaliero';
  const tariffLabel = tariffLabels[tariffType] || 'Giornaliera';
  const tariffPrice = rentalData.tariffPrice != null ? Number(rentalData.tariffPrice) : (rentalData.dailyRate != null ? Number(rentalData.dailyRate) : 25);
  yPosition = addHeader('CONDIZIONI DI NOLEGGIO', yPosition);
  yPosition = addField('Data Inizio', new Date(rentalData.startDate).toLocaleDateString('it-IT'), margin, yPosition);
  yPosition = addField('Ora Inizio', new Date(rentalData.startDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }), margin, yPosition);
  yPosition = addField('Data Fine', new Date(rentalData.endDate).toLocaleDateString('it-IT'), margin, yPosition);
  yPosition = addField('Ora Fine', new Date(rentalData.endDate).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }), margin, yPosition);
  yPosition = addField('Durata', `${String(Math.ceil((new Date(rentalData.endDate) - new Date(rentalData.startDate)) / (1000 * 60 * 60 * 24)))} giorni`, margin, yPosition);
  yPosition = addField(`Tariffa ${tariffLabel}`, `€ ${tariffPrice.toFixed(2)}`, margin, yPosition);
  yPosition = addField('Importo Totale', `€ ${String(rentalData.totalAmount?.toFixed(2) || '0.00')}`, margin, yPosition);
  yPosition += 6;
  
  
  // Clausole del contratto
  yPosition = addHeader('CONDIZIONI DI NOLEGGIO', yPosition);
  
  const clauses = [
    '1. Il noleggio e l\'uso della bicicletta a pedalata assistita o del monopattino (di seguito anche il veicolo) presuppone la conoscenza ed accettazione incondizionata da parte del Cliente del presente regolamento, delle tariffe, degli orari di apertura e chiusura del Noleggiatore come indicati.',
    '2. Per ottenere il noleggio di un veicolo il Cliente deve presentare preventivamente al Noleggiatore un documento di identità valido, e formalizzare il contratto.',
    '3. L\'uso del veicolo è riservato ai maggiori di 14 anni con obbligo di caschetto, oppure ai maggiori di 18 anni con utilizzo del casco consigliato.',
    '4. L\'uso del veicolo presuppone l\'idoneità fisica e la perizia tecnica di chi intende condurla. Pertanto, il Cliente noleggiando il veicolo dichiara di essere dotato di adeguata capacità e di appropriata competenza, senza porre alcuna riserva e dichiarando di tenere a tal fine manlevato il Noleggiatore da qualsiasi conseguenza.',
    '5. Il veicolo è da utilizzarsi esclusivamente come mezzo di trasporto ed è da trattarsi con attenzione, buon senso e diligenza, in modo da evitare danni sia alla stessa che ai relativi accessori. È vietato l\'utilizzo del veicolo per svolgere attività commerciali ne è possibile cederla in uso ad altri soggetti.',
    '6. Il Cliente è responsabile del veicolo fino alla sua restituzione al Noleggiatore; è inoltre responsabile dei danni causati a sé stesso, al veicolo, a terzi e cose durante l\'uso del mezzo. Al Noleggiatore non potrà essere richiesta nessuna forma di indennizzo.',
    '7. Il Cliente è tenuto a rispettare le norme del Codice della strada. Il Noleggiatore declina ogni forma di responsabilità in caso di utilizzo improprio del mezzo o di mancato rispetto delle norme del Codice della strada.',
    '8. Il Noleggiatore può effettuare controlli agli Utenti durante l\'uso dei veicoli e può richiederne la restituzione se ravvisa condizioni di un utilizzo improprio del veicolo. Il Noleggiatore potrà rifiutare il noleggio a persone in stato di ubriachezza o sotto effetto di sostanze stupefacenti, nonché per altri motivi a suo insindacabile giudizio.',
    '9. In caso di danni il Noleggiatore richiederà al Cliente la somma necessaria per il ripristino originale del veicolo, sulla base del preventivo del fornitore rilasciato dalla casa madre. In caso di furto sarà addebitato al Cliente il costo dell\'intero veicolo',
    '10. Il veicolo dovrà essere riconsegnato, nel rispetto degli orari comunicati (ore 9-19 di ciascun giorno), nello stesso luogo in cui è stata noleggiato. Il veicolo è considerato riconsegnato solo se restituito direttamente al Noleggiatore; non può essere considerato riconsegna il parcheggio del veicolo al di fuori del punto di noleggio durante l\'orario di chiusura. La mancata restituzione del veicolo senza preventiva comunicazione o comunque non motivata da casi eccezionali, sarà denunciata alla Autorità Giudiziaria.',
    '11. Il Cliente potrà richiedere al Noleggiatore un intervento per il ritiro del veicolo, il cui costo non è compreso nel canone di noleggio; il Noleggiatore non è comunque tenuto all\'intervento, ed il mancato intervento non può essere causa di inadempimento alcuno imputabile al Noleggiatore.',
    '12. Il Cliente dovrà corrispondere il corrispettivo anticipatamente al momento del noleggio ed è comunque tenuto al pagamento delle eventuali maggiori somme dovute per la detenzione del veicolo oltre il tempo richiesto e comunque fino a quando non si presenterà per la chiusura del contratto di noleggio, oltre ad eventuali addebiti per danni, totali o parziali.',
    '13. In caso di furto del veicolo, il Cliente dovrà presentare al Noleggiatore una breve relazione sull\'accaduto, specificando tutte le condizioni di luogo, tempo e modi in cui è avvenuto l\'evento, fornendo altresì massima collaborazione al Noleggiatore affinché quest\'ultimo possa presentare formale denuncia presso gli organi competenti. Tutti i costi sono a carico del Cliente.',
    '14. Per quanto non previsto e disciplinato nel presente contratto il rapporto tra le Parti (contraenti) è regolato dalle norme del codice civile. Per qualsiasi controversia insorta tra le Parti il foro competente è in via esclusiva quello di Torre Annunziata.',
    '15. Il Cliente con la sottoscrizione del presente contratto dichiara di essere stato informato che i dati personali forniti e/o acquisiti da parte del Noleggiatore nell\'ambito della sua attività, necessari per dare esecuzione ai servizi offerti, saranno trattati nel rispetto della normativa in materia di privacy e dei princìpi di correttezza, di liceità, di trasparenza e di tutela della Sua riservatezza e dei Suoi diritti, in osservanza di quanto prevede il Regolamento Europeo 2016/679 (in sigla GDPR). Il Titolare del trattamento è ' + (companyData.name || 'ELEMOVE SRLS') + ' con sede in ' + (companyData.address || 'VIA RAIOLA 19, 80053 CASTELLAMMARE DI STABIA (NA)') + '.'
  ];
  
  yPosition = addSection('TERMINI E CONDIZIONI', clauses, yPosition);
  
  // Clausole di approvazione
  if (yPosition > pageHeight - 100) {
    doc.addPage();
    yPosition = margin;
  }
  
  yPosition += 6;
  yPosition = addText('APPROVAZIONE CLAUSOLE CONTRATTUALI', margin, yPosition, { fontSize: 12, fontStyle: 'bold', color: primaryColor });
  yPosition += 3;
  
  const approvedClauses = [
    'Clausola 3 (responsabilità sui minorenni)',
    'Clausola 4 (idoneità fisica e perizia)',
    'Clausola 6 (esonero di responsabilità del Noleggiatore per danni a terzi o al Cliente)',
    'Clausola 7 (esonero di responsabilità del Noleggiatore in caso di utilizzo improprio)',
    'Clausola 9 (risarcimento danni subiti dal mezzo)',
    'Clausola 13 (obblighi in caso di furto)',
    'Clausola 14 (risarcimento danni in caso di furto)',
    'Clausola 15 (foro esclusivo)',
    'Clausola 16 (informativa privacy)'
  ];
  
  approvedClauses.forEach(clause => {
    yPosition = addText(`✓ ${clause}`, margin + 10, yPosition, { fontSize: 9 });
    yPosition += 2;
  });
  
  yPosition += 8;
  
  // Firma
  yPosition = addText('Ai fini e per gli effetti degli art. 1341 - 1342 c.c. si intendono specificatamente approvate le seguenti clausole: 3 (responsabilità sui minorenni), 4 (idoneità fisica e perizia), 6 (esonero di responsabilità del Noleggiatore per danni a terzi o al Cliente), 7 (esonero di responsabilità del Noleggiatore in caso di utilizzo improprio), 10 (risarcimento danni subiti dal mezzo), 14(obblighi in caso di furto), 15 (risarcimento danni in caso di furto), 16 (foro esclusivo), 17 (informativa privacy).', margin, yPosition, { fontSize: 9 });
  yPosition += 6;
  
  yPosition = addText('Castellammare di Stabia lì, _______________________________', margin, yPosition, { fontSize: 10 });
  yPosition += 3;
  yPosition = addText('Letto, approvato e sottoscritto in ogni sua parte.', margin, yPosition, { fontSize: 10, fontStyle: 'bold' });
  yPosition += 8;
  
  // Firma Noleggiatore
  yPosition = addText('IL NOLEGGIATORE', margin, yPosition, { fontSize: 10, fontStyle: 'bold' });
  yPosition += 3;
  yPosition = addText(`${companyData.name || 'ELEMOVE SRLS'}`, margin, yPosition, { fontSize: 9 });
  yPosition += 10;
  yPosition = addText('________________________', margin, yPosition, { fontSize: 10 });
  yPosition = addText('Firma e timbro', margin, yPosition, { fontSize: 8 });
  yPosition += 8;
  
  // Firma Cliente
  yPosition = addText('IL CLIENTE', margin, yPosition, { fontSize: 10, fontStyle: 'bold' });
  yPosition += 3;
  yPosition = addText(`${customerData.firstName} ${customerData.lastName}`, margin, yPosition, { fontSize: 9 });
  yPosition += 10;
  yPosition = addText('________________________', margin, yPosition, { fontSize: 10 });
  yPosition = addText('Firma', margin, yPosition, { fontSize: 8 });
  
  // Salva il PDF
  const fileName = `Contratto_Noleggio_${customerData.firstName}_${customerData.lastName}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  
  return doc;
};
